/**
 * Timbrica Processing History
 * Standalone module — stores last 10 processed files per tool in IndexedDB.
 * Shows history only on the tool page where the file was processed.
 */
(function() {
    'use strict';

    var DB_NAME = 'timbrica_history';
    var DB_VERSION = 1;
    var STORE = 'files';
    var MAX_ENTRIES = 10;
    var MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB — skip bigger files
    var db = null;

    // ── IndexedDB helpers ──

    // Sentry 117541575 (Mobile Safari 18.7 iOS, /ru/speaker-cleaner):
    // `indexedDB.open()` throws SYNCHRONOUSLY with `SecurityError:
    // IDBFactory.open() called in an invalid security context` on iOS
    // private browsing and certain embedded WebViews. The original code
    // wired `req.onerror` but a sync throw bypasses the request object
    // entirely. Wrap in try/catch and disable the history feature
    // silently — it's a nice-to-have, not core functionality. Sentry's
    // beforeSend filter (app.blade.php) drops the SecurityError so we
    // don't double-count this case as a "bug".
    var _dbDisabled = false;
    function openDB(cb) {
        if (_dbDisabled) return cb('IndexedDB unavailable');
        if (db) return cb(null, db);
        // window.indexedDB ACCESS itself throws SecurityError on Firefox 115
        // when third-party cookies are blocked / the page is in a private
        // window with strict tracking protection (TIMBRICA-FRONTEND-8P).
        // The existence check belongs INSIDE the try so the access doesn't
        // skip the disable-and-cache path below.
        try {
            if (!window.indexedDB) return cb('IndexedDB not supported');
            var req = indexedDB.open(DB_NAME, DB_VERSION);
            // iOS Safari 18.x can fire upgradeneeded/success with an EMPTY
            // request.result after the backing connection dies (private mode,
            // page teardown mid-open). `d.objectStoreNames` on undefined was a
            // hard crash; an empty result means the DB is unusable for this
            // session — disable the whole module (every public API already
            // no-ops on openDB failure), never hand out a half-open handle.
            req.onupgradeneeded = function(e) {
                var d = e.target && e.target.result;
                if (!d) { _dbDisabled = true; return; }
                try {
                    if (!d.objectStoreNames.contains(STORE)) {
                        var store = d.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
                        store.createIndex('ts', 'ts');
                    }
                } catch (err) {
                    // Dead connection surfacing as InvalidStateError — same class.
                    _dbDisabled = true;
                }
            };
            req.onsuccess = function(e) {
                var d = e.target && e.target.result;
                if (!d || _dbDisabled) {
                    // Upgrade was skipped or result is empty: the store may not
                    // exist. Treat as unavailable rather than crash later.
                    _dbDisabled = true;
                    cb('IndexedDB unavailable');
                    return;
                }
                db = d;
                cb(null, db);
            };
            req.onerror = function() { cb('DB open failed'); };
        } catch (e) {
            // Private mode / sandboxed context — disable for the session.
            _dbDisabled = true;
            try {
                if (window.__ae) window.__ae('feature_use', {
                    action: 'idb_unavailable',
                    err: String((e && e.name) || 'unknown') + ':' + String((e && e.message) || '').substring(0, 80)
                });
            } catch(_){}
            cb('IndexedDB unavailable');
        }
    }

    function saveFile(blob, name, slug) {
        if (!blob || blob.size > MAX_FILE_SIZE || blob.size === 0) return;
        openDB(function(err, d) {
            if (err) return;
            try {
                var tx = d.transaction(STORE, 'readwrite');
                var store = tx.objectStore(STORE);
                store.add({
                    name: name,
                    slug: slug || '',
                    size: blob.size,
                    type: blob.type || 'application/octet-stream',
                    blob: blob,
                    ts: Date.now()
                });
                tx.oncomplete = function() { cleanup(d); };
            } catch(e) {
                db = null;
            }
        });
    }

    // Keep only last MAX_ENTRIES per tool
    function cleanup(d) {
        try {
            var tx = d.transaction(STORE, 'readwrite');
            var store = tx.objectStore(STORE);
            var idx = store.index('ts');
            var countReq = store.count();
            countReq.onsuccess = function() {
                var total = countReq.result;
                if (total <= MAX_ENTRIES * 5) return; // global cap
                var toDelete = total - MAX_ENTRIES * 5;
                var cursor = idx.openCursor();
                cursor.onsuccess = function(e) {
                    var c = e.target.result;
                    if (c && toDelete > 0) {
                        c.delete();
                        toDelete--;
                        c.continue();
                    }
                };
            };
        } catch(e) {}
    }

    function getAll(cb, filterSlug) {
        openDB(function(err, d) {
            if (err) return cb([]);
            try {
                var tx = d.transaction(STORE, 'readonly');
                var store = tx.objectStore(STORE);
                var idx = store.index('ts');
                var req = idx.openCursor(null, 'prev');
                var results = [];
                req.onsuccess = function(e) {
                    var c = e.target.result;
                    if (c) {
                        if ((!filterSlug || c.value.slug === filterSlug) && results.length < MAX_ENTRIES) {
                            results.push({
                                id: c.value.id,
                                name: c.value.name,
                                slug: c.value.slug,
                                size: c.value.size,
                                type: c.value.type,
                                blob: c.value.blob,
                                ts: c.value.ts
                            });
                        }
                        c.continue();
                    } else {
                        cb(results);
                    }
                };
                req.onerror = function() { cb([]); };
            } catch(e) { cb([]); }
        });
    }

    function deleteEntry(id, cb) {
        openDB(function(err, d) {
            if (err) return cb && cb();
            // transaction() throws synchronously on a dying/storeless
            // connection — degrade to a no-op like every other API here.
            try {
                var tx = d.transaction(STORE, 'readwrite');
                tx.objectStore(STORE).delete(id);
                tx.oncomplete = function() { cb && cb(); };
            } catch (e) { cb && cb(); }
        });
    }

    function clearAll(cb) {
        openDB(function(err, d) {
            if (err) return cb && cb();
            try {
                var tx = d.transaction(STORE, 'readwrite');
                tx.objectStore(STORE).clear();
                tx.oncomplete = function() { cb && cb(); };
            } catch (e) { cb && cb(); }
        });
    }

    function downloadEntry(entry) {
        if (!entry || !entry.blob) return;
        var url = URL.createObjectURL(entry.blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = entry.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(function() { URL.revokeObjectURL(url); }, 5000);
        try { if (window.__ae) window.__ae('feature_use', {f: 'history_redownload', slug: entry.slug, sizeMB: Math.round(entry.size / 1048576)}); } catch(e) {}
    }

    // ── Patch FFmpegProcessor.download() ──

    function patchDownload() {
        var FP = window.FFmpegProcessor;
        if (!FP || !FP.download || FP.__historyPatched) return;
        var orig = FP.download;
        var slug = (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
        FP.download = function(blob, name) {
            try { saveFile(blob, name, slug); } catch(e) {}
            return orig.apply(this, arguments);
        };
        FP.__historyPatched = true;
    }

    if (window.FFmpegProcessor) patchDownload();
    window.addEventListener('DOMContentLoaded', patchDownload);
    var _patchTimer = setInterval(function() {
        if (window.FFmpegProcessor && !window.FFmpegProcessor.__historyPatched) {
            patchDownload();
            clearInterval(_patchTimer);
        }
    }, 2000);
    setTimeout(function() { clearInterval(_patchTimer); }, 30000);

    // ── Format helpers ──

    function fmtSize(b) {
        if (b < 1024) return b + ' B';
        if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
        if (b < 1073741824) return (b / 1048576).toFixed(1) + ' MB';
        return (b / 1073741824).toFixed(2) + ' GB';
    }

    function fmtTime(ts) {
        var d = new Date(ts);
        var now = Date.now();
        var diff = Math.round((now - ts) / 1000);
        // `(x || {}).p`, not `x?.p` — this file is loaded on EVERY page, and
        // optional chaining is a PARSE error on pre-Chrome-80 engines: the
        // whole module fails to load rather than this one call failing.
        var _hl = window.__historyL || {};
        if (diff < 60) return _hl.just_now || 'Just now';
        if (diff < 3600) return Math.floor(diff / 60) + (_hl.min_ago || ' min ago');
        if (diff < 86400) return Math.floor(diff / 3600) + (_hl.hr_ago || ' hr ago');
        var dd = String(d.getDate()).padStart(2, '0');
        var mm = String(d.getMonth() + 1).padStart(2, '0');
        return dd + '.' + mm + '.' + d.getFullYear();
    }

    function fmtExt(name) {
        var dot = name.lastIndexOf('.');
        return dot > -1 ? name.substring(dot + 1).toUpperCase() : '';
    }

    // ── UI Rendering ──

    function renderPanel(container) {
        if (!container) return;
        var currentSlug = (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
        // Only show on tool pages that have a slug
        if (!currentSlug) { container.style.display = 'none'; return; }
        // Filter by current tool slug — only show files processed on THIS tool
        getAll(function(entries) {
            if (!entries.length) {
                container.innerHTML = '';
                container.style.display = 'none';
                return;
            }

            var L = window.__historyL || {};
            var html = '<div class="th-panel">';
            html += '<div class="th-header">';
            html += '<span class="th-title">' + (L.title || 'Recent files') + '</span>';
            html += '<button type="button" class="th-clear" data-th-clear>' + (L.clear_all || 'Clear') + '</button>';
            html += '</div>';
            html += '<div class="th-list">';

            entries.forEach(function(f) {
                html += '<div class="th-item" data-th-id="' + f.id + '">';
                html += '<div class="th-item-info">';
                html += '<span class="th-item-ext">' + fmtExt(f.name) + '</span>';
                html += '<div class="th-item-meta">';
                html += '<span class="th-item-name" title="' + f.name.replace(/"/g, '&quot;') + '">' + f.name + '</span>';
                html += '<span class="th-item-details">' + fmtSize(f.size) + ' · ' + fmtTime(f.ts) + '</span>';
                html += '</div></div>';
                html += '<div class="th-item-actions">';
                html += '<button type="button" class="th-dl" data-th-dl="' + f.id + '" title="' + (L.download || 'Download') + '">';
                html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
                html += '</button>';
                html += '<button type="button" class="th-del" data-th-del="' + f.id + '" title="' + (L.delete_item || 'Delete') + '">';
                html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
                html += '</button>';
                html += '</div></div>';
            });

            html += '</div></div>';
            container.innerHTML = html;
            container.style.display = '';

            // Event delegation
            container.onclick = function(e) {
                var dlBtn = e.target.closest('[data-th-dl]');
                if (dlBtn) {
                    var id = parseInt(dlBtn.getAttribute('data-th-dl'));
                    var entry = entries.find(function(f) { return f.id === id; });
                    if (entry) downloadEntry(entry);
                    return;
                }
                var delBtn = e.target.closest('[data-th-del]');
                if (delBtn) {
                    var did = parseInt(delBtn.getAttribute('data-th-del'));
                    deleteEntry(did, function() { renderPanel(container); });
                    return;
                }
                var clearBtn = e.target.closest('[data-th-clear]');
                if (clearBtn) {
                    clearAll(function() { renderPanel(container); });
                }
            };
        }, currentSlug);
    }

    // ── Public API ──
    window.__tHistory = {
        getAll: getAll,
        deleteEntry: deleteEntry,
        clearAll: clearAll,
        download: downloadEntry,
        render: renderPanel,
        save: saveFile
    };

    // Auto-render if container exists
    document.addEventListener('DOMContentLoaded', function() {
        var el = document.getElementById('processingHistory');
        if (el) renderPanel(el);
        // Re-render after any download (new file in history)
        window.addEventListener('file-downloaded', function() {
            setTimeout(function() {
                var el2 = document.getElementById('processingHistory');
                if (el2) renderPanel(el2);
            }, 500);
        });
    });
})();
